import { PropsWithChildren } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { useApplication } from '@eneco/common/application';
import logger from '@eneco/common/log';
import RichText from '@eneco/components/RichText/RichText';
import {
  GenderDto,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberContactDetails,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddresses,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { Accordion, Bucket, NotificationBox, Text, TextLink } from '@eneco/sparky';
import { styled } from '@eneco/sparky/stitches';
import { useContent } from '@sitecore/common';
import { ContactDataRendering } from '@sitecore/types/ContactData';

import DataErrorNotification from '../../../../components/DataErrorNotification';
import { LoadingListSpinner } from '../../../../components/Loading/LoadingListSpinner';
import ReadFormField from '../../../../components/ReadFormField';
import { useBillingAccount } from '../../../../hooks/useBillingAccount';
import useBusLabel from '../../../../hooks/useBusLabel';
import { useDateFormatter } from '../../../../hooks/useDateFormatter';
import { formattedAddress } from '../../../../utils/addresses/addressFormatter';
import { COUNTRIES_MAP } from '../../../../utils/countries';
import { LANGUAGES_MAP } from '../../../../utils/language';

const ContactData = () => {
  const { fields } = useContent<ContactDataRendering>();
  const { selectedAccount, isProspect, isCurrentAccountReader } = useSelfServiceAccount();
  const { data: session } = useSession();
  const { shortDate } = useDateFormatter();
  const { language } = useApplication();
  const busLabel = useBusLabel();

  const {
    data: unwrappedContactData,
    isLoading: isLoadingContact,
    error: errorContact,
  } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberContactDetails,
    {
      path: {
        accountNumber: selectedAccount.crmAccountNumber,
      },
    },
    [`/accounts/${selectedAccount.crmAccountNumber}/contact/details`],
    session,
  );

  const { data: unwrappedContractData, isLoading: isLoadingContract } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddresses,
    {
      path: {
        accountNumber: selectedAccount.crmAccountNumber,
      },
    },
    [`/accounts/${selectedAccount.crmAccountNumber}/delivery-addresses`],
    session,
  );

  const contactData = unwrapData(unwrappedContactData);
  const contractData = unwrapData(unwrappedContractData);
  const { hasBillingAccount } = useBillingAccount({ addressIdentifier: contractData?.addresses[0]?.id || '' });

  const hasContract = !!contractData?.addresses && contractData.addresses.length > 0;

  const genderMap: { [key in GenderDto]: string } = {
    Male: fields.genderMaleText.value,
    Female: fields.genderFemaleText.value,
    Unspecified: '',
  };

  const { contactAddress } = contactData || {};
  let birthDate = contactData?.dateOfBirth;
  try {
    if (birthDate) birthDate = shortDate(birthDate);
  } catch {
    logger.error('EUSxiq', `Unable to parse birthDate of account ${contactData?.accountNumber}`);
  }

  if (isProspect) {
    return null;
  }

  if (!isLoadingContact && !!errorContact && !contactData) {
    return <DataErrorNotification />;
  }

  const hasExistingCompanyNumber = !!contactData?.companyData?.companyNumber;
  const companyTypeOptions = fields.companyTypeFRList?.value?.options.concat(
    fields.companyTypeList?.value?.options ?? [],
  );
  const companyTypeLabel =
    companyTypeOptions?.find(option => option.value === contactData?.companyData?.formatType)?.label ??
    contactData?.companyData?.formatType;

  return (
    <>
      <Bucket title={fields.title.value}>
        <Bucket.Content>
          <ReadFormField
            label={fields.languageLabel.value}
            value={contactData?.language ? LANGUAGES_MAP[contactData.language][language] : undefined}
            isLoading={isLoadingContact}
          />
          <ReadFormField
            label={fields.nameLabel.value}
            value={
              contactData?.gender
                ? `${genderMap[contactData.gender] || ''} ${contactData?.firstName} ${contactData?.lastName}`
                : `${contactData?.firstName || ''} ${contactData?.lastName || ''}`
            }
            isLoading={isLoadingContact}
          />
          <ReadFormField
            label={fields.customerNumberLabel.value}
            value={selectedAccount.crmAccountNumber}
            isLoading={isLoadingContact}
          />
          <ReadFormField label={fields.birthDateLabel.value} value={birthDate} isLoading={isLoadingContact} />
          <ReadFormField
            label={fields.phoneNumberLabel.value}
            value={contactData?.telephoneNumber}
            isLoading={isLoadingContact}
          />
          <ReadFormField
            label={fields.mobilePhoneNumberLabel.value}
            value={contactData?.mobileNumber}
            isLoading={isLoadingContact}
          />
          <ReadFormField
            label={fields.emailAddressLabel.value}
            value={contactData?.email}
            isLoading={isLoadingContact}
          />
          <ReadFormField
            label={fields.addressLabel.value}
            value={
              contactAddress
                ? formattedAddress({
                    street: contactAddress.street,
                    houseNumber: contactAddress.houseNumber,
                    bus: contactAddress.bus,
                    busLabel: busLabel,
                    zipCode: contactAddress.zipCode,
                    municipality: contactAddress.municipality,
                    country: contactAddress.country
                      ? COUNTRIES_MAP.find(c => c.value === contactAddress.country)?.label[language]
                      : undefined,
                  })
                : null
            }
            isLoading={isLoadingContact}
          />
          <NotificationBox
            isAlert={false}
            title={fields.moveHelpNotification?.value.title}
            text={<RichText html={fields.moveHelpNotification?.value.content} />}
          />
          {!isCurrentAccountReader && hasBillingAccount && (
            <Bucket.Actions>
              <TextLink
                href={fields.editContactDataLink.value.href}
                target={fields.editContactDataLink.value.target}
                emphasis="high">
                {fields.editContactDataLink.value.text}
              </TextLink>
            </Bucket.Actions>
          )}
        </Bucket.Content>
      </Bucket>
      {isLoadingContact && <LoadingListSpinner />}
      {contactData?.customerType === 'Residential' && (
        <>
          <Bucket title={fields.familyBondTitle.value}>
            <Bucket.Content>
              <ReadFormField label={fields.membershipLabel.value} value={contactData?.gezinsBondNumber}></ReadFormField>
              {!isCurrentAccountReader && (
                <Bucket.Actions>
                  <TextLink
                    href={fields.editFamilyBondLink.value.href}
                    target={fields.editFamilyBondLink.value.target}
                    emphasis="high">
                    {fields.editFamilyBondLink.value.text}
                  </TextLink>
                </Bucket.Actions>
              )}
            </Bucket.Content>
          </Bucket>
          <SwitchToB2CExpandable isLoading={isLoadingContract} hasContract={hasContract} />
        </>
      )}
      {contactData?.customerType === 'SOHO' && (
        <Bucket title={fields.companyDataTitle.value}>
          <Bucket.Content>
            <TextWrapperUseMultipleLines>
              <ReadFormField label={fields.companyDataCompanyNameLabel.value} value={contactData.companyData?.name} />
            </TextWrapperUseMultipleLines>

            <ReadFormField
              label={fields.companyDataCompanyTypeLabel.value}
              value={companyTypeLabel}
              isLoading={isLoadingContact}
            />
            {contactData.companyData?.vatType === 'CompanyNumberRequested' && (
              <ReadFormField
                label={fields.companyDataCompanyNumberLabel.value}
                value={fields.companyDataNumberRequestLabel?.value}
                isLoading={isLoadingContact}
              />
            )}
            {contactData.companyData?.vatType !== 'CompanyNumberRequested' && (
              <ReadFormField
                label={fields.companyDataCompanyNumberLabel.value}
                value={contactData.companyData?.companyNumber}
                isLoading={isLoadingContact}
              />
            )}
            {hasExistingCompanyNumber && !!fields.companyNameChangeNotification?.value.content && (
              <NotificationBox
                isAlert={false}
                variant={fields.companyNameChangeNotification?.value.variant ?? 'info'}
                title={fields.companyNameChangeNotification?.value.title}
                text={<RichText html={fields.companyNameChangeNotification?.value.content} />}
              />
            )}
            {!isCurrentAccountReader && !hasExistingCompanyNumber && (
              <Bucket.Actions>
                <TextLink
                  emphasis="high"
                  href={fields.editCompanyDataLink.value.href}
                  target={fields.editCompanyDataLink.value.target}>
                  {fields.editCompanyDataLink.value.text}
                </TextLink>
              </Bucket.Actions>
            )}
          </Bucket.Content>
        </Bucket>
      )}
    </>
  );
};

const SwitchToB2CExpandable = ({ isLoading, hasContract }: { isLoading: boolean; hasContract: boolean }) => {
  const { fields } = useContent<ContactDataRendering>();

  if (isLoading) {
    return <LoadingListSpinner />;
  }

  if (!hasContract) {
    return null;
  }

  return (
    <Accordion>
      <Accordion.Item heading={fields.changeCustomerTypeTitle.value}>
        <Text size={'BodyM'}>
          <RichText
            html={fields.changeCustomerTypeContent.value}
            replacements={{
              strong: (props: PropsWithChildren) => (
                <Text weight={'bold'} display={'inline'} whiteSpace={'nowrap'}>
                  {props.children}
                </Text>
              ),
            }}
          />
        </Text>
      </Accordion.Item>
    </Accordion>
  );
};
const TextWrapperUseMultipleLines = styled('div', {
  whiteSpace: 'normal',
  overflow: 'hidden',

  '& span': {
    display: 'inline',
    wordWrap: 'break-word',
    wordBreak: 'break-word',
  },
});
export default ContactData;
